import { createApi } from '@reduxjs/toolkit/query/react';

import { baseQuery } from 'src/services/redux/api';
import type {
  ChangeForgottenPasswordRequest,
  ForgotPasswordRequest,
  LogInRequest,
  LogInResponse,
  UpdatePasswordRequest,
} from './types';
import { UpdateUserPasswordRequest } from './types';

export const authApi = createApi({
  baseQuery,
  endpoints: (builder) => ({
    changeForgottenPassword: builder.mutation<
      void,
      ChangeForgottenPasswordRequest
    >({
      query: (body) => ({
        body,
        method: 'POST',
        url: 'user/forgot-password/approve',
      }),
    }),
    forgotPassword: builder.mutation<void, ForgotPasswordRequest>({
      query: (body) => ({
        body,
        method: 'POST',
        url: 'user/forgot-password',
      }),
    }),
    logIn: builder.mutation<LogInResponse, LogInRequest>({
      transformResponse: (res: { data: LogInResponse }) => res.data,
      query: (body) => ({
        body,
        method: 'POST',
        url: 'auth/sign-in',
      }),
    }),
    acceptPrivacyPolicy: builder.mutation<void, void>({
      query: () => ({
        method: 'POST',
        url: 'user/accept-privacy-policy',
      }),
    }),
    logOut: builder.mutation<void, void>({
      query: () => ({
        method: 'GET',
        url: 'auth/logout',
      }),
    }),
    updatePassword: builder.mutation<void, UpdatePasswordRequest>({
      query: (body) => ({
        body,
        method: 'PUT',
        url: 'user/update/password',
      }),
    }),
    updateUserPassword: builder.mutation<void, UpdateUserPasswordRequest>({
      query: ({ id, ...body }) => ({
        body,
        method: 'PUT',
        url: `user/update/password/${id}`,
      }),
    }),
    samlLookup: builder.query<{ firmId: number } | null, string>({
      transformResponse: (res: { data: { firmId: number } | null }) => res.data,
      query: (email) => ({
        method: 'GET',
        url: `auth/saml/lookup?email=${encodeURIComponent(email)}`,
      }),
    }),
    samlExchange: builder.mutation<LogInResponse, string>({
      transformResponse: (res: { data: LogInResponse }) => res.data,
      query: (code) => ({
        body: { code },
        method: 'POST',
        url: 'auth/saml/exchange',
      }),
    }),
  }),
  reducerPath: 'authApi',
});

export const {
  useAcceptPrivacyPolicyMutation,
  useChangeForgottenPasswordMutation,
  useForgotPasswordMutation,
  useLogInMutation,
  useLogOutMutation,
  useUpdatePasswordMutation,
  useUpdateUserPasswordMutation,
  useLazySamlLookupQuery,
  useSamlExchangeMutation,
} = authApi;
